/**
 * A rail the user can resize by dragging its inner edge.
 *
 * Three things this does that a naive drag handle does not:
 *
 * PERSISTS. Width is remembered per rail in localStorage. A width you have to
 * set again every visit is worse than no control at all, because you pay the
 * adjustment cost repeatedly and never bank it.
 *
 * CLAMPS. Both a minimum and a maximum, so a rail cannot be dragged to nothing
 * — leaving no grip to drag back — and cannot starve the centre column, which
 * is the only part of the page carrying the day's work.
 *
 * KEYBOARD. The grip is a real focusable separator with arrow-key resizing and
 * Home/End for the extremes. A pointer-only control is not a control for
 * everybody, and `role="separator"` with aria-valuenow is what makes the
 * current width announceable rather than invisible.
 *
 * The drag listens on window rather than the element, so the pointer can leave
 * the 5px grip mid-drag without the resize stopping — the single most common
 * way a hand-rolled splitter feels broken.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

const STORAGE_PREFIX = 'trendy_ws_rail_'
const STEP = 16
const STEP_COARSE = 64

function readStored(id, fallback) {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + id)
    const n = raw == null ? NaN : parseInt(raw, 10)
    return Number.isFinite(n) ? n : fallback
  } catch {
    // Private windows and blocked site data both throw here. A rail that
    // cannot remember its width must still render at a sensible one.
    return fallback
  }
}

export default function ResizableRail({
  id,
  side = 'left',
  defaultWidth,
  min = 160,
  max = 480,
  className = '',
  style = {},
  children,
}) {
  const clamp = useCallback((w) => Math.min(max, Math.max(min, w)), [min, max])
  const [width, setWidth] = useState(() => clamp(readStored(id, defaultWidth)))
  const [dragging, setDragging] = useState(false)
  const widthRef = useRef(width)
  widthRef.current = width

  const commit = useCallback((next) => {
    const w = clamp(next)
    setWidth(w)
    try {
      localStorage.setItem(STORAGE_PREFIX + id, String(w))
    } catch {
      // Not remembering is survivable; failing to resize is not.
    }
  }, [clamp, id])

  const onPointerDown = useCallback((e) => {
    e.preventDefault()
    setDragging(true)
    const startX = e.clientX
    const startWidth = widthRef.current

    const move = (ev) => {
      // A left rail grows when the pointer moves right; a right rail is the
      // mirror of that.
      const delta = side === 'left' ? ev.clientX - startX : startX - ev.clientX
      const w = clamp(startWidth + delta)
      setWidth(w)
      widthRef.current = w
    }
    const up = () => {
      setDragging(false)
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
      document.body.style.cursor = ''
      document.body.style.userSelect = ''
      commit(widthRef.current)
    }

    document.body.style.cursor = 'ew-resize'
    // Without this a drag across text selects it, which looks like a bug.
    document.body.style.userSelect = 'none'
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }, [side, clamp, commit])

  const onKeyDown = useCallback((e) => {
    const grow = side === 'left' ? 'ArrowRight' : 'ArrowLeft'
    const shrink = side === 'left' ? 'ArrowLeft' : 'ArrowRight'
    const step = e.shiftKey ? STEP_COARSE : STEP

    if (e.key === grow) { e.preventDefault(); commit(widthRef.current + step) }
    else if (e.key === shrink) { e.preventDefault(); commit(widthRef.current - step) }
    else if (e.key === 'Home') { e.preventDefault(); commit(min) }
    else if (e.key === 'End') { e.preventDefault(); commit(max) }
  }, [side, commit, min, max])

  // A window that shrinks below the stored width would otherwise leave the
  // centre column with nothing.
  useEffect(() => {
    const onResize = () => {
      const ceiling = Math.min(max, Math.round(window.innerWidth * 0.4))
      if (widthRef.current > ceiling) commit(ceiling)
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [commit, max])

  return (
    <aside
      // h-full + min-h-0 so the rail fills the shell and its CHILD can scroll.
      // Without min-h-0 a flex child refuses to shrink below its content, the
      // overflow never engages, and the composer ends up at the bottom of the
      // document instead of the bottom of the rail.
      className={`relative shrink-0 hidden lg:flex flex-col h-full min-h-0 ${className}`}
      style={{ width: `${width}px`, ...style }}
    >
      {children}
      <div
        role="separator"
        aria-orientation="vertical"
        aria-label={`Resize ${side} panel`}
        aria-valuenow={width}
        aria-valuemin={min}
        aria-valuemax={max}
        tabIndex={0}
        data-dragging={dragging}
        onPointerDown={onPointerDown}
        onKeyDown={onKeyDown}
        className="ws-grip"
        style={side === 'left' ? { right: -2 } : { left: -2 }}
      />
    </aside>
  )
}
